// Poster: oversized heavy capitals, one loud bright colour against a strong
// ground, blocks of colour and price-list rules. From the Fade barber concepts.
// Colours: paper is the bright, accent the strong ground, ink near black.
import { flow, textBlock, brand, footer, tag, photo, photoBand, pairBlock, pairStory, panel, display, label, body, strong, hero, widthOf, nameTag , codeTicket, finePrint } from '../kit'

const base = (env) => ({ fr: env.frame, c: env.post.content, k: env.theme.c, w: widthOf(env.frame) })
const times = (c) => (c.times || []).filter(Boolean).slice(0, 3)

export default {
  freeSlots(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    const list = times(c)
    flow(p, fr, [
      brand(env, fr, k.paper),
      textBlock(c.eyebrow, () => ({ ...label(env, k.paper), x: fr.left, w }), { gap: 96, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, k.paper, 160 * s), x: fr.left - 6, w: w + 6, maxLines: 3 }), { gap: 30, slot: 'headline' }),
      {
        gap: 60, flex: 1,
        draw: (pp, y, s) => panel(pp, {
          x: fr.left, y, w, bg: k.paper, pad: 40,
          content: (q, yy) => {
            const d = q.text(env.day, { ...display(env, k.ink, 76 * s), x: fr.left + 40, y: yy, w: w - 80, maxLines: 1, slot: 'date' })
            let end = d.bottom
            list.forEach((t) => {
              q.line(fr.left + 40, end + 18, fr.right - 40, end + 18, k.ink, 3)
              end = q.text(t, { ...strong(env, k.ink, 56), x: fr.left + 40, y: end + 34, w: w - 80, maxLines: 1, slot: 'times' }).bottom
            })
            if (c.more) end = q.text('More times available', { ...label(env, k.ink), x: fr.left + 40, y: end + 22, w: w - 80, slot: 'more' }).bottom
            return end
          },
        }),
      },
      footer(env, fr, k.paper),
    ])
  },

  oneSlot(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    flow(p, fr, [
      brand(env, fr, k.paper),
      textBlock(c.headline, s => ({ ...display(env, k.paper, 160 * s), x: fr.left - 6, w: w + 6, maxLines: 3 }), { gap: 150, slot: 'headline' }),
      {
        gap: 60, flex: 1,
        draw: (pp, y, s) => panel(pp, {
          x: fr.left, y, w, bg: k.paper, pad: 40,
          content: (q, yy) => {
            const t = q.text([env.day, c.time].filter(Boolean).join(' / '), { ...display(env, k.ink, 72 * s), x: fr.left + 40, y: yy, w: w - 80, maxLines: 2, slot: 'time' })
            return c.service ? q.text(c.service, { ...label(env, k.ink), x: fr.left + 40, y: t.bottom + 18, w: w - 80, slot: 'service' }).bottom : t.bottom
          },
        }),
      },
      textBlock(c.line, () => ({ ...label(env, k.paper), x: fr.left, w, maxLines: 2 }), { gap: 40, slot: 'line' }),
      footer(env, fr, k.paper),
    ])
  },

  freshWork(p, env) {
    const { fr, c, k } = base(env)
    photoBand(p, env, {
      key: 'main', photoH: 880, band: k.paper, pad: 44,
      blocks: (f) => [
        f.story ? brand(env, f, k.ink) : null,
        textBlock(c.detail, () => ({ ...label(env, k.ink), x: f.left, w: widthOf(f) }), { gap: f.story ? 26 : 0, slot: 'detail' }),
        textBlock(c.headline, s => ({ ...display(env, k.ink, 96 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 3 }), { gap: 18, slot: 'headline' }),
        footer(env, f, k.ink),
      ],
    })
    if (!fr.story) nameTag(p, env, 54, 54, k.ink, k.paper)
  },

  beforeAfter(p, env) {
    const { fr, c, k } = base(env)
    const words = (f) => [
      textBlock(c.headline, s => ({ ...display(env, k.ink, 112 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 2 }), { slot: 'headline' }),
      textBlock(c.detail, () => ({ ...label(env, k.ink), x: f.left, w: widthOf(f) }), { gap: 16, slot: 'detail' }),
      footer(env, f, k.ink),
    ]
    if (fr.story) {
      pairStory(p, env, { bg: k.paper, tagBg: k.ink, tagFg: k.paper, blocks: words })
      return
    }
    p.rect(0, 0, fr.W, fr.H, k.paper)
    const [head, detail, foot] = words(fr)
    flow(p, fr, [brand(env, fr, k.ink), pairBlock(env, fr, { h: 640, gap: 12, tagBg: k.ink, tagFg: k.paper }), head && { ...head, gap: 40 }, detail, foot])
  },

  review(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    const stars = Math.max(1, Math.min(5, Number(c.rating) || 5))
    flow(p, fr, [
      brand(env, fr, k.paper),
      { gap: 110, draw: (pp, y, s) => { pp.stars(fr.left, y, Math.round(84 * s), stars, k.paper, 16); return y + Math.round(84 * s) } },
      textBlock(c.quote ? `“${c.quote}”` : '', s => ({ ...hero(env, k.paper, 92 * s), x: fr.left, w, maxLines: 8, maxH: 600 * s }), { gap: 50, slot: 'quote' }),
      textBlock(c.name, () => ({ ...label(env, k.paper), x: fr.left, w }), { gap: 50, flex: 1, slot: 'name' }),
      textBlock(c.service, () => ({ ...body(env, k.paper, 34), x: fr.left, w, maxLines: 1 }), { gap: 8, slot: 'service' }),
      footer(env, fr, k.paper),
    ])
  },

  team(p, env) {
    const { fr, c, k, w } = base(env)
    const words = (f) => [
      textBlock(c.name, s => ({ ...display(env, k.ink, 132 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 1 }), { gap: 36, slot: 'name' }),
      textBlock(c.role, () => ({ ...label(env, k.ink), x: f.left, w: widthOf(f) }), { gap: 12, slot: 'role' }),
      textBlock(c.line, () => ({ ...body(env, k.ink, 38), x: f.left, w: widthOf(f), maxLines: 2 }), { gap: 16, slot: 'line' }),
      footer(env, f, k.ink),
    ]
    if (fr.story) {
      photoBand(p, env, {
        key: 'main', band: k.paper, pad: 44,
        blocks: (f) => [brand(env, f, k.ink), textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: f.left, w: widthOf(f) }), { gap: 24, slot: 'eyebrow' }), ...words(f).map((b, i) => (i === 0 && b ? { ...b, gap: 20 } : b))],
      })
      return
    }
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      {
        gap: 36,
        draw: (pp, y, s) => {
          const h = Math.round(620 * s)
          photo(pp, env, 'main', { x: fr.left, y, w, h })
          if (c.eyebrow) tag(pp, env, c.eyebrow, fr.left + 18, y + 18, k.ink, k.paper)
          return y + h
        },
      },
      ...words(fr),
    ])
  },

  priceList(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    const page = (env.slide && env.slide.page) || 0
    const items = (c.items || []).filter(it => it && it.name).slice(page * 4, page * 4 + 4)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.title, s => ({ ...display(env, k.ink, 150 * s), x: fr.left - 6, w: w + 6, maxLines: 2 }), { gap: 100, slot: 'title' }),
      {
        gap: 70, flex: 1,
        draw: (pp, y) => {
          let yy = y
          items.forEach((it) => {
            pp.line(fr.left, yy + 1, fr.right, yy + 1, k.ink, 3)
            const n = pp.text(it.name, { ...strong(env, k.ink, 42), upper: true, tracking: 0.02, x: fr.left, y: yy + 30, w: w - 240, maxLines: 1, slot: 'items' })
            const d = it.detail ? pp.text(it.detail, { ...body(env, k.ink, 32), x: fr.left, y: n.bottom + 8, w: w - 240, maxLines: 1 }) : n
            const pr = pp.text(it.price || '', { ...display(env, k.ink, 52), upper: false, x: fr.right - 230, y: yy + 26, w: 230, align: 'right', maxLines: 1, minSize: 36, slot: 'items' })
            yy = Math.max(d.bottom, pr.bottom) + 28
          })
          return yy
        },
      },
      textBlock(c.note, () => ({ ...body(env, k.ink, 34), x: fr.left, w, maxLines: 2 }), { gap: 20, slot: 'note' }),
      footer(env, fr, k.ink),
    ])
  },

  tips(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.label, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 90, slot: 'label' }),
      textBlock(c.number, s => ({ ...display(env, k.accent, 280 * s), x: fr.left - 6, w: w + 6, maxLines: 1, minSize: 120 }), { gap: 6, slot: 'number' }),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 104 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 24, slot: 'headline' }),
      textBlock(c.body, () => ({ ...body(env, k.ink, 42), x: fr.left, w: w - 30, maxLines: 5 }), { gap: 36, slot: 'body' }),
      footer(env, fr, k.ink),
    ])
  },

  question(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    flow(p, fr, [
      brand(env, fr, k.paper),
      { gap: 50, draw: (pp, y, s) => pp.text('?', { ...display(env, k.paper, 330 * s), x: fr.left - 12, y, w: 400, maxLines: 1, minSize: 160 }).bottom - 20 * s },
      textBlock(c.headline, s => ({ ...display(env, k.paper, 116 * s), x: fr.left - 4, w: w + 4, maxLines: 4 }), { gap: 24, slot: 'headline' }),
      textBlock(c.body, () => ({ ...body(env, k.paper, 42), x: fr.left, w: w - 30, maxLines: 5 }), { gap: 40, slot: 'body' }),
      footer(env, fr, k.paper),
    ])
  },

  // An announcement: a season, a date, some news, as loud as it goes
  announce(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    flow(p, fr, [
      brand(env, fr, k.paper),
      textBlock(c.eyebrow, () => ({ ...label(env, k.paper), x: fr.left, w }), { gap: 96, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, k.paper, 176 * s), x: fr.left - 6, w: w + 6, maxLines: 4 }), { gap: 30, flex: 1, slot: 'headline' }),
      textBlock(c.body, () => ({ ...body(env, k.paper, 42), x: fr.left, w: w - 30, maxLines: 4 }), { gap: 44, slot: 'body' }),
      footer(env, fr, k.paper),
    ])
  },

  offer(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    flow(p, fr, [
      brand(env, fr, k.paper),
      textBlock(c.eyebrow, () => ({ ...label(env, k.paper), x: fr.left, w }), { gap: 100, slot: 'eyebrow' }),
      textBlock(c.value, s => ({ ...display(env, k.paper, 230 * s), x: fr.left - 6, w: w + 6, maxLines: 2 }), { gap: 20, slot: 'value' }),
      {
        gap: 50, flex: 1,
        draw: (pp, y, s) => panel(pp, {
          x: fr.left, y, w, bg: k.paper, pad: 40,
          content: (q, yy) => {
            const sv = q.text(c.service || '', { ...display(env, k.ink, 60 * s), x: fr.left + 40, y: yy, w: w - 80, maxLines: 2, slot: 'service' })
            const pr = q.text(c.price || '', { ...strong(env, k.ink, 38), x: fr.left + 40, y: sv.bottom + 16, w: w - 80, maxLines: 2, slot: 'price' })
            return c.dates ? q.text(c.dates, { ...body(env, k.ink, 34), x: fr.left + 40, y: pr.bottom + 8, w: w - 80, maxLines: 1, slot: 'dates' }).bottom : pr.bottom
          },
        }),
      },
      codeTicket(env, fr, k.paper),
      finePrint(env, fr, k.paper),
      footer(env, fr, k.paper),
    ])
  },

  giftVouchers(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 100, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 168 * s), x: fr.left - 6, w: w + 6, maxLines: 3 }), { gap: 24, slot: 'headline' }),
      {
        gap: 60, flex: 1,
        draw: (pp, y, s) => {
          const cw = Math.min(w, 820)
          const ch = Math.round(440 * s)
          pp.rect(fr.left, y, cw, ch, k.accent)
          pp.text(env.theme.name, { ...label(env, k.paper), x: fr.left + 44, y: y + 40, w: cw - 360 })
          pp.text('Gift voucher', { ...label(env, k.paper), x: fr.left + cw - 360, y: y + 40, w: 316, align: 'right' })
          pp.text(c.from || '', { ...display(env, k.paper, 108 * s), x: fr.left + 44, y: y + ch - 44 - 108 * s * 0.94, w: cw - 88, maxLines: 1, slot: 'from' })
          return y + ch
        },
      },
      footer(env, fr, k.ink),
    ])
  },
}

